import { TCartApi } from "../shared/cart-contents";
import { TPromoCodeRequest } from "../shared/promo-types";
import { HelperName, TId, WidgetName } from "../shared/types";

/* ------------------------------------------------------------------------------------------------------------------ */

type NetworkEventDetail =
  | { url: string; method: string; type: "fetch"; response: Response }
  | { url: string; method: string; type: "xhr"; xhr: XMLHttpRequest };

/**
 * beamnetworkcall
 * Emitted by the Beam network listener whenever any API calls are made on the page
 */
export class BeamNetworkCallEvent extends CustomEvent<NetworkEventDetail> {
  static eventName = "beamnetworkcall" as const;
  constructor(detail: NetworkEventDetail) {
    super(BeamNetworkCallEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */

type NonprofitSelectDetail = {
  selectionId?: string;
  selectedNonprofitId?: number | null;
  nonprofitName?: string | null;
  source?: WidgetName | null;
  timestamp?: Date;
};

/**
 * beamnonprofitselect
 * Emitted when the end user selects a nonprofit in the Beam widget, and when the previous selection was restored
 * automatically
 */
export class BeamNonprofitSelectEvent extends CustomEvent<NonprofitSelectDetail> {
  static eventName = "beamnonprofitselect" as const;
  constructor(detail: NonprofitSelectDetail) {
    super(BeamNonprofitSelectEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */

type CartChangeDetail = TCartApi & {
  cartId?: string;
  subtotal?: number;
  itemCount?: number;
  currencyCode?: string;
};

/**
 * beamcartchange
 * Emitted when Beam detects that cart contents have changed, and when items are added to the cart for the first time
 */
export class BeamCartChangeEvent extends CustomEvent<CartChangeDetail> {
  static eventName = "beamcartchange" as const;
  constructor(detail: CartChangeDetail) {
    super(BeamCartChangeEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */

type CartCreatedDetail = CartChangeDetail;

/**
 * beamcartcreated
 * Emitted when a cart is created in Beam for the first time, but NOT when the cart is updated
 * Beam's cart expiration rules are used to determine when a returning visit counts as the same cart
 */
export class BeamCartCreatedEvent extends CustomEvent<CartCreatedDetail> {
  static eventName = "beamcartcreated" as const;
  constructor(detail: CartCreatedDetail) {
    super(BeamCartCreatedEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */

type OrderCreatedDetail = {
  orderId: TId;
  transactionId: TId;
  cartTotal: number;
  currencyCode: string;
  selectionId?: string | null;
  cartId?: string | null;
  beamCartId?: string | null;
  storeId?: number | null;
  postalCode?: string | null;
  countryCode?: string | null;
  remoteSessionId?: string | null;
  transactionNonprofitId?: number | null;
  orderProcessedAt?: string | null;
};

/**
 * beamordercreated
 * Emitted when an order is registered with Beam for the first time, but NOT on repeat visits, page refreshes, etc.
 */
export class BeamOrderCreatedEvent extends CustomEvent<OrderCreatedDetail> {
  static eventName = "beamordercreated" as const;

  constructor(detail: OrderCreatedDetail) {
    super(BeamOrderCreatedEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */

/**
 * beamstatuschange
 * Emitted when the Beam config object has finished initializing along with all plugins
 */
type BeamStatusChangeEventDetail = { status: "pending" } | { status: "error"; error: Error } | { status: "ready" };
export class BeamStatusChangeEvent extends CustomEvent<BeamStatusChangeEventDetail> {
  static eventName = "beamstatuschange" as const;

  constructor(detail: BeamStatusChangeEventDetail) {
    super(BeamStatusChangeEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */
type NonprofitSelectionRemovedDetail = {
  previousNonprofitId?: number | null;
  newNonprofitId?: number | null;
  selectionId?: string;
  timestamp?: Date;
};

/**
 * beamnonprofitselectionremoved
 * Emitted when the nonprofit selection is removed from deselection or due to nonprofit cohort change
 */
export class BeamNonprofitSelectionRemovedEvent extends CustomEvent<NonprofitSelectionRemovedDetail> {
  static eventName = "beamnonprofitselectionremoved" as const;
  constructor(detail: NonprofitSelectionRemovedDetail) {
    super(BeamNonprofitSelectionRemovedEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */
type PromoStoredDetail = {
  promoCodes: TPromoCodeRequest;
};
/**
 * Emitted when a URL with query parameters is sanitized and stored in localStorage
 *
 * @event beampromocodesstored
 */
export class BeamPromoCodesStoredEvent extends CustomEvent<PromoStoredDetail> {
  static eventName = "beampromocodesstored";

  constructor(detail: PromoStoredDetail) {
    super(BeamPromoCodesStoredEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */

type WidgetStoredPromosDetail = {
  source?: WidgetName | HelperName | null;
};
/**
 * Emitted when a widget stores the url or manual promoCodes in local storage
 *
 * @event beamwidgetstoredpromocodes
 */
export class BeamWidgetStoredPromoCodesEvent extends CustomEvent<WidgetStoredPromosDetail> {
  static eventName = "beamwidgetstoredpromocodes" as const;

  constructor(detail: WidgetStoredPromosDetail) {
    super(BeamWidgetStoredPromoCodesEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */
type WidgetInitialNonprofitSyncDetail = {
  nonprofitId?: number | null;
  nonprofitIdChanged?: boolean;
  selectionId?: string;
  selectionIdChanged?: boolean;
};

/**
 * [Shopify Specific] - Emitted the first time a widget restores nonprofit selection from custom attributes
 *
 * @event beamwidgetinitialnonprofitsync
 */
export class BeamWidgetInitialNonprofitSyncEvent extends CustomEvent<WidgetInitialNonprofitSyncDetail> {
  static eventName = "beamwidgetinitialnonprofitsync" as const;

  constructor(detail: WidgetInitialNonprofitSyncDetail) {
    super(BeamWidgetInitialNonprofitSyncEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */
type BeamWidgetInitialNonprofitSyncCancelDetail = {
  cancelled?: boolean;
};

/**
 * [Shopify Specific] - Allows WidgetInitialNonprofitSyncDetail to be cancelled asynchronously
 *
 * @event beamwidgetinitialnonprofitsynccancel
 */
export class BeamWidgetInitialNonprofitSyncCancelEvent extends CustomEvent<BeamWidgetInitialNonprofitSyncCancelDetail> {
  static eventName = "beamwidgetinitialnonprofitsynccancel" as const;

  constructor(detail: BeamWidgetInitialNonprofitSyncCancelDetail) {
    super(BeamWidgetInitialNonprofitSyncCancelEvent.eventName, { detail, bubbles: true, composed: true });
  }
}

/* ------------------------------------------------------------------------------------------------------------------ */
type BeamStatsigInitializedDetail = {
  showBeam: boolean;
  stableId?: string;
};

/**
 * Fired when Statsig experiment assignments for Beam are initialized
 *
 * @event beamstatsiginitialized
 */
export class BeamStatsigInitializedEvent extends CustomEvent<BeamStatsigInitializedDetail> {
  static eventName = "beamstatsiginitialized" as const;

  constructor(detail: BeamStatsigInitializedDetail) {
    super(BeamStatsigInitializedEvent.eventName, { detail, bubbles: true, composed: true });
  }
}
